import React, { useCallback, useEffect, useRef, useState } from 'react'
import QRCode from 'qrcode'
import { Camera, Mic, Keyboard, Mouse, Smartphone, Wifi } from 'lucide-react'
import AutoRotateIcon from '../vision/AutoRotateIcon'
import {
    CAMERA_ASPECTS,
    loadCameraAspect,
    saveCameraAspect,
    loadCameraOrientation,
    loadOrientationMode,
    setManualOrientation,
    enableAutoOrientation,
    ORIENTATION_EVENT,
    AUTO_ORIENTATION_HINT,
    listCameraOptions,
    MOBILE_CAMERA_ID,
} from '../vision/cameraConfig'
import { makeRoomCode } from '../webrtc/signalingConfig'
import { PHONE_CHROME_HINT } from '../PhonePairNote'
import { getMobileStream } from '../webrtc/mobileStream'
import {
    startMobilePairing,
    stopMobilePairing,
    mobilePairingActive,
    mobilePairingRoom,
    mobileConnected,
    setPairingStatusCallback,
} from '../webrtc/mobileLink'
import styles from './AiCodeConfigure.module.css'
import ChromeNote from '../PhonePairNote'

const loadSelectedFeatures = () => {
    try {
        return JSON.parse(sessionStorage.getItem('aiCodeSelectedFeatures')) || []
    } catch {
        return []
    }
}

// Reusable Header for all input cards to ensure perfect alignment
const InputHeader = ({ icon: Icon, label, statusText, statusType, actionBtn }) => (
    <div className={styles.inputHeader}>
        <div className={styles.inputIcon}>
            <Icon size={24} strokeWidth={1.8} color="#5b5bd6" />
        </div>
        <div className={styles.inputInfo}>
            <p className={styles.inputName}>{label}</p>
            <p className={`${styles.inputStatus} ${styles[`status_${statusType}`]}`}>
                {statusText}
            </p>
        </div>
        {actionBtn && <div className={styles.inputAction}>{actionBtn}</div>}
    </div>
)

// Live device/permission status for a media input (camera or microphone).
const MediaInput = ({ icon: Icon, label, kind, storageKey }) => {
    const [status, setStatus] = useState('idle') // idle|connecting|connected|error
    const [devices, setDevices] = useState([])
    const [activeId, setActiveId] = useState(
        sessionStorage.getItem(storageKey) || ''
    )

    const connectedKey = kind === 'camera' ? 'aiCameraConnected' : 'aiMicConnected'

    // Re-read the device list and reflect the REAL state: a physically removed
    // device disappears from enumerateDevices, so we can flip back to
    // "disconnected" instead of clinging to a stale "Connected" (bug 60).
    const enumerate = useCallback(async () => {
        try {
            const list = await navigator.mediaDevices.enumerateDevices()
            const wanted = kind === 'camera' ? 'videoinput' : 'audioinput'
            const all = list.filter((d) => d.kind === wanted)
            const found = all
                .filter((d) => d.deviceId)
                .map((d, i) => ({
                    deviceId: d.deviceId,
                    label: d.label || `${label} ${i + 1}`,
                }))
            setDevices(found)
            // If the chosen device was unplugged but another one remains, fall
            // back to it (like the camera) so a stale deviceId can't later cause
            // a "device not found" error.
            setActiveId((cur) => {
                if (found.length && cur && !found.some((d) => d.deviceId === cur)) {
                    const next = found[0].deviceId
                    sessionStorage.setItem(storageKey, next)
                    return next
                }
                return cur
            })
            // A device present but unlabelled means permission not yet granted;
            // no entry at all means nothing is plugged in.
            const present = all.length > 0
            const granted = all.some((d) => d.label)
            if (!present) {
                setStatus('gone')
                sessionStorage.removeItem(connectedKey)
            } else if (granted) {
                setStatus('connected')
                sessionStorage.setItem(connectedKey, 'true')
            } else {
                // Present but not yet permitted. Bring a previously-gone device
                // back to "connect me", but never downgrade a live connection on
                // a transient empty-label read.
                setStatus((prev) => (prev === 'gone' ? 'idle' : prev))
            }
            return found
        } catch (e) {
            return []
        }
    }, [kind, label, connectedKey, storageKey])

    useEffect(() => {
        let cancelled = false
        const probe = async () => {
            try {
                if (navigator.permissions?.query) {
                    const name = kind === 'camera' ? 'camera' : 'microphone'
                    const res = await navigator.permissions.query({ name })
                    if (!cancelled && res.state === 'granted') enumerate()
                }
            } catch (e) { }
            if (!cancelled) enumerate()
        }
        probe()
        // Live-track hot-plug / unplug so the status stays truthful.
        const onChange = () => enumerate()
        try {
            navigator.mediaDevices.addEventListener('devicechange', onChange)
        } catch (e) { }
        return () => {
            cancelled = true
            try {
                navigator.mediaDevices.removeEventListener('devicechange', onChange)
            } catch (e) { }
        }
    }, [kind, enumerate])

    const connect = async () => {
        setStatus('connecting')
        try {
            const constraints = kind === 'camera' ? { video: true } : { audio: true }
            const stream = await navigator.mediaDevices.getUserMedia(constraints)
            const settings = (kind === 'camera'
                ? stream.getVideoTracks()
                : stream.getAudioTracks())[0]?.getSettings?.() || {}
            if (settings.deviceId) {
                setActiveId(settings.deviceId)
                sessionStorage.setItem(storageKey, settings.deviceId)
            }
            stream.getTracks().forEach((t) => t.stop())
            sessionStorage.setItem(
                kind === 'camera' ? 'aiCameraConnected' : 'aiMicConnected',
                'true'
            )
            setStatus('connected')
            enumerate()
        } catch (e) {
            setStatus('error')
        }
    }

    const onSelect = (e) => {
        setActiveId(e.target.value)
        sessionStorage.setItem(storageKey, e.target.value)
    }

    let statusType = status === 'connected' ? 'ok'
        : (status === 'error' || status === 'gone') ? 'err' : 'idle'
    let statusText = status === 'connected' ? 'Connected'
        : status === 'gone' ? `No ${label.toLowerCase()} detected — reconnect the device`
            : status === 'error' ? 'Permission denied. Allow access and retry.'
                : status === 'connecting' ? 'Connecting…'
                    : 'Not connected'

    return (
        <div className={styles.inputCard}>
            <InputHeader
                icon={Icon}
                label={label}
                statusText={statusText}
                statusType={statusType}
                actionBtn={status !== 'connected' && (
                    <button
                        className={styles.inputConnectBtn}
                        onClick={connect}
                        disabled={status === 'connecting'}
                    >
                        {status === 'connecting' ? 'Connecting…' : 'Connect'}
                    </button>
                )}
            />
            {status === 'connected' && devices.length > 1 && (
                <div className={styles.inputBodyExpanded}>
                    <select className={styles.inputSelect} value={activeId} onChange={onSelect}>
                        {devices.map((d) => (
                            <option key={d.deviceId} value={d.deviceId}>{d.label}</option>
                        ))}
                    </select>
                </div>
            )}
        </div>
    )
}

// Camera FPS control
const FpsControl = () => {
    const [fps, setFps] = useState(() => parseInt(sessionStorage.getItem('aiCameraFps')) || 16)
    const onFps = (e) => {
        setFps(e.target.value)
        sessionStorage.setItem('aiCameraFps', e.target.value)
    }
    return (
        <div className={styles.settingBlock}>
            <div className={styles.settingLabelWrap}>
                <p className={styles.settingLabel}>Frames Per Second</p>
                <span className={styles.settingValue}>{fps} FPS</span>
            </div>
            <input
                className={styles.fpsSlider}
                type="range" min="1" max="30"
                value={fps} onChange={onFps}
            />
        </div>
    )
}

// Camera frame aspect ratio & orientation
const RatioControl = ({ showOrientation }) => {
    const [aspect, setAspect] = useState(loadCameraAspect)
    const [orientation, setOrientation] = useState(loadCameraOrientation)
    const [orientMode, setOrientMode] = useState(loadOrientationMode)
    const onPick = (key) => { saveCameraAspect(key); setAspect(key); }
    // Auto follows the phone's real rotation; Landscape/Portrait pin it.
    const pinOrient = (o) => { setManualOrientation(o); setOrientation(o); setOrientMode('manual'); }
    const autoOrient = () => { enableAutoOrientation(); setOrientMode('auto'); }

    // Reflect auto-driven orientation changes (phone rotating while on this tab).
    useEffect(() => {
        const sync = () => {
            setOrientation(loadCameraOrientation())
            setOrientMode(loadOrientationMode())
        }
        window.addEventListener(ORIENTATION_EVENT, sync)
        return () => window.removeEventListener(ORIENTATION_EVENT, sync)
    }, [])

    return (
        <div className={styles.settingBlock}>
            <p className={styles.settingLabel}>Aspect Ratio</p>
            <div className={styles.ratioGroup}>
                {CAMERA_ASPECTS.map((a) => (
                    <button
                        key={a.key} type="button"
                        className={`${styles.ratioBtn} ${aspect === a.key ? styles.ratioBtnOn : ''}`}
                        onClick={() => onPick(a.key)}
                    >
                        {a.label}
                    </button>
                ))}
            </div>
            {showOrientation && (
                <div className={styles.ratioGroup} style={{ marginTop: '8px' }}>
                    <button
                        type="button"
                        className={`${styles.ratioBtn} ${orientMode === 'auto' ? styles.ratioBtnOn : ''}`}
                        onClick={autoOrient}
                        style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}
                    >
                        <AutoRotateIcon size={15} />
                        Auto
                    </button>
                    {[
                        { key: 'landscape', label: 'Landscape', rotate: 90 },
                        { key: 'portrait', label: 'Portrait', rotate: 0 },
                    ].map((o) => (
                        <button
                            key={o.key} type="button"
                            className={`${styles.ratioBtn} ${orientMode === 'manual' && orientation === o.key ? styles.ratioBtnOn : ''}`}
                            onClick={() => pinOrient(o.key)}
                            style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}
                        >
                            <Smartphone size={15} style={{ transform: `rotate(${o.rotate}deg)` }} />
                            {o.label}
                        </button>
                    ))}
                </div>
            )}
            {showOrientation && orientMode === 'auto' && (
                <p className={styles.settingNote}>{AUTO_ORIENTATION_HINT}</p>
            )}
        </div>
    )
}

// Camera input with a source dropdown (including Phone Camera).
const CameraInput = () => {
    const [options, setOptions] = useState([])
    const [selected, setSelected] = useState(() => sessionStorage.getItem('aiCameraDeviceId') || '')
    const [localGranted, setLocalGranted] = useState(false)
    const [phoneStatus, setPhoneStatus] = useState('idle') // idle|pairing|connected
    const [qr, setQr] = useState('')
    const [room, setRoom] = useState('')
    const videoRef = useRef(null)
    const [linkCopied, setLinkCopied] = useState(false);

    const refreshOptions = useCallback(async () => {
        const opts = await listCameraOptions()
        setOptions(opts)
    }, [])

    const attachPreview = useCallback((stream) => {
        const s = stream || getMobileStream()
        if (s && videoRef.current) {
            videoRef.current.srcObject = s
            videoRef.current.play().catch(() => { })
        }
    }, [])

    useEffect(() => {
        refreshOptions()
            ; (async () => {
                try {
                    if (navigator.permissions?.query) {
                        const res = await navigator.permissions.query({ name: 'camera' })
                        if (res.state === 'granted') {
                            setLocalGranted(true)
                            refreshOptions()
                        }
                    }
                } catch (e) { }
            })()
        if (mobileConnected()) setPhoneStatus('connected')
        else if (mobilePairingActive()) {
            setPhoneStatus('pairing')
            const code = mobilePairingRoom()
            setRoom(code)
            // Rebuild the QR image so it survives navigating away and back —
            // the pairing session is still live, only the picture was lost.
            const url = `${window.location.origin}/mobilecamera?room=${code}`
            QRCode.toDataURL(url, { width: 170, margin: 1 })
                .then(setQr)
                .catch(() => { })
        }
        setPairingStatusCallback((s, stream) => {
            if (s === 'connected') {
                setPhoneStatus('connected')
                attachPreview(stream)
            }
        })
        return () => setPairingStatusCallback(null)
    }, [refreshOptions, attachPreview])

    useEffect(() => {
        if (selected === MOBILE_CAMERA_ID && phoneStatus === 'connected') attachPreview()
    }, [selected, phoneStatus, attachPreview])

    const grantLocal = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ video: true })
            stream.getTracks().forEach((t) => t.stop())
            sessionStorage.setItem('aiCameraConnected', 'true')
            setLocalGranted(true)
            refreshOptions()
        } catch (e) { }
    }

    const startPhone = async () => {
        const code = makeRoomCode()
        setRoom(code)
        setPhoneStatus('pairing')
        const url = `${window.location.origin}/mobilecamera?room=${code}`
        try {
            setQr(await QRCode.toDataURL(url, { width: 170, margin: 1 }))
        } catch (e) { }
        startMobilePairing(code, (s, stream) => {
            if (s === 'connected') {
                setPhoneStatus('connected')
                attachPreview(stream)
            }
        })
    }

    const disconnectPhone = () => {
        stopMobilePairing()
        setPhoneStatus('idle')
        setQr('')
        setRoom('')
    }

    // Drop the current pairing and start a fresh one (new QR/code) — the way out
    // when the phone couldn't join (e.g. it was on a different Wi-Fi).
    const retryPhone = () => {
        stopMobilePairing()
        startPhone()
    }

    const onSelect = (e) => {
        const value = e.target.value
        setSelected(value)
        sessionStorage.setItem('aiCameraDeviceId', value)
        if (value === MOBILE_CAMERA_ID) {
            if (phoneStatus === 'idle') startPhone()
        } else if (!localGranted) {
            grantLocal()
        }
    }

    const isPhone = selected === MOBILE_CAMERA_ID

    // Determine Status
    let statusType = 'idle'
    let statusText = 'Not connected'
    if (isPhone) {
        if (phoneStatus === 'connected') { statusType = 'ok'; statusText = 'Phone paired via WebRTC' }
        else if (phoneStatus === 'pairing') { statusType = 'idle'; statusText = `Waiting for phone... Code: ${room}` }
    } else {
        if (localGranted) { statusType = 'ok'; statusText = 'Camera connected' }
    }

    const actionBtn = isPhone ? (
        phoneStatus === 'idle' ? (
            <button className={styles.inputConnectBtn} onClick={startPhone}>Connect Phone</button>
        ) : (
            <span style={{ display: 'inline-flex', gap: '8px' }}>
                {phoneStatus === 'pairing' && (
                    <button className={styles.inputConnectBtn} onClick={retryPhone}>
                        New code
                    </button>
                )}
                <button className={styles.inputConnectBtnOutline} onClick={disconnectPhone}>Disconnect</button>
            </span>
        )
    ) : (
        !localGranted && (
            <button className={styles.inputConnectBtn} onClick={grantLocal}>Connect</button>
        )
    )

    return (
        <div className={styles.inputCard}>
            <InputHeader
                icon={Camera}
                label="Camera"
                statusText={statusText}
                statusType={statusType}
                actionBtn={actionBtn}
            />

            <div className={styles.inputBodyExpanded}>
                <select className={styles.inputSelect} value={selected} onChange={onSelect}>
                    {!selected && <option value="">Select camera…</option>}
                    {options.map((o) => (
                        <option key={o.deviceId} value={o.deviceId}>{o.label}</option>
                    ))}
                </select>

                {isPhone && phoneStatus === 'pairing' && qr && (
                    <div className={styles.qrContainer}>

                        {/* =====================================================
            QR CODE
        ====================================================== */}

                        <div className={styles.qrCodeSection}>

                            <div className={styles.qrCodeWrapper}>
                                <img
                                    src={qr}
                                    alt="Scan to connect phone"
                                    className={styles.cameraQr}
                                />
                            </div>

                            <div className={styles.qrCodeLabel}>
                                <Smartphone
                                    size={16}
                                    strokeWidth={1.8}
                                />

                                <span>
                                    Scan with your phone
                                </span>
                            </div>

                        </div>


                        {/* =====================================================
            DETAILS
        ====================================================== */}

                        <div className={styles.qrDetails}>

                            {/* Main instruction */}

                            <div className={styles.qrTitle}>
                                Connect your phone camera
                            </div>

                            <div className={styles.qrSubtitle}>
                                Scan the QR code to open the camera on your phone.
                            </div>


                            {/* =================================================
                REQUIREMENTS
            ================================================== */}

                            <div className={styles.qrRequirements}>

                                {/* Wi-Fi */}

                                <div className={styles.qrRequirement}>
                                    <Wifi
                                        size={15}
                                        strokeWidth={1.8}
                                    />

                                    <span>
                                        Phone and this device must be on the same Wi-Fi network.
                                    </span>
                                </div>


                                {/* Chrome */}

                                <ChromeNote
                                    style={{
                                        margin: 0,
                                    }}
                                />

                            </div>


                            {/* =================================================
                MANUAL LINK
            ================================================== */}

                            <div className={styles.qrManualLink}>

                                <div className={styles.qrManualHeader}>

                                    <span>
                                        Can't scan the QR code?
                                    </span>

                                    <button
                                        type="button"
                                        className={styles.qrLinkCopyBtn}
                                        onClick={async (e) => {

                                            const url =
                                                `https://edu.plode.org/mobilecamera?room=${room}`;

                                            try {
                                                await navigator.clipboard.writeText(url);

                                                setLinkCopied(true);

                                                setTimeout(() => {
                                                    setLinkCopied(false);
                                                }, 2000);
                                            } catch (error) {
                                                console.error('Failed to copy link:', error);
                                            }
                                        }}
                                    >
                                        {linkCopied ? '✓ Copied' : 'Copy link'}
                                    </button>

                                </div>


                                {/* URL */}

                                <div className={styles.qrLinkInputBox}>

                                    <input
                                        type="text"
                                        readOnly
                                        value={`https://edu.plode.org/mobilecamera?room=${room}`}
                                        className={styles.qrLinkInput}
                                        onClick={(e) => e.target.select()}
                                    />

                                </div>

                            </div>

                        </div>

                    </div>
                )}

                {isPhone && phoneStatus === 'connected' && (
                    <video ref={videoRef} autoPlay playsInline muted className={styles.cameraPreview} />
                )}

                {/* Combined Settings Panel for a cleaner look */}
                <div className={styles.settingsPanel}>
                    <RatioControl showOrientation={isPhone} />
                    <div className={styles.settingsDivider} />
                    <FpsControl />
                </div>
            </div>
        </div>
    )
}

// Keyboard/mouse can't be probed or hot-plug-detected by the browser (there's
// no web API for HID keyboard/mouse presence). So instead of a hard-coded
// "Ready to use", confirm the device actually works by waiting for its first
// real event, then report "Ready to use". (True unplug detection isn't possible
// on the web — bug 60.)
const ReadyInput = ({ icon, label, kind }) => {
    const [seen, setSeen] = useState(false)
    useEffect(() => {
        const events = kind === 'mouse' ? ['mousemove', 'mousedown'] : ['keydown']
        const on = () => setSeen(true)
        events.forEach((e) => window.addEventListener(e, on, { passive: true }))
        return () =>
            events.forEach((e) => window.removeEventListener(e, on))
    }, [kind])
    return (
        <div className={styles.inputCard}>
            <InputHeader
                icon={icon}
                label={label}
                statusText={
                    seen
                        ? 'Ready to use'
                        : kind === 'mouse'
                            ? 'Move the mouse to confirm'
                            : 'Press any key to confirm'
                }
                statusType={seen ? 'ok' : 'idle'}
            />
        </div>
    )
}

// Main Inputs Panel component
const InputsPanel = () => {
    const selected = loadSelectedFeatures()
    const hasCam = selected.includes('inputs/camera') || selected.some((k) => k.startsWith('ai/') && k !== 'ai/voice')
    const hasMic = selected.includes('inputs/microphone') || selected.includes('ai/voice')
    const hasKeyboard = selected.includes('inputs/keyboard')
    const hasMouse = selected.includes('inputs/mouse')

    if (!hasCam && !hasMic && !hasKeyboard && !hasMouse) {
        return (
            <div className={styles.emptyState}>
                <p className={styles.placeholder}>No input selected.</p>
                <p className={styles.subPlaceholder}>Go back to "Select Feature" to add one.</p>
            </div>
        )
    }

    return (
        <div className={styles.inputsPanel}>
            {hasCam && <CameraInput />}
            {hasMic && <MediaInput icon={Mic} label="Microphone" kind="microphone" storageKey="aiMicDeviceId" />}
            {hasKeyboard && <ReadyInput icon={Keyboard} label="Keyboard" kind="keyboard" />}
            {hasMouse && <ReadyInput icon={Mouse} label="Mouse" kind="mouse" />}
        </div>
    )
}

export default InputsPanel